'use client';

import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

const INDICATOR_BUMP_DURATION_MS = 600;
const ITEM_SHAKE_DURATION_MS = 420;

export type MobileNavIndicator = { left: number; width: number; visible: boolean };

export type UseMobileNavAnimationsResult = {
  indicatorBump: boolean;
  shakeId: string | null;
  indicator: MobileNavIndicator;
};

export function useMobileNavAnimations(
  activeId: string | null,
  containerRef: RefObject<HTMLElement | null>,
  itemRefs: RefObject<Record<string, HTMLElement | null>>,
): UseMobileNavAnimationsResult {
  const [isIndicatorBumping, setIsIndicatorBumping] = useState(false);
  const [shakingItemId, setShakingItemId] = useState<string | null>(null);
  const [indicator, setIndicator] = useState<MobileNavIndicator>({
    left: 0,
    width: 0,
    visible: false,
  });

  useEffect(() => {
    if (!activeId) {
      return;
    }

    setIsIndicatorBumping(false);
    setShakingItemId(activeId);

    const frameId = window.requestAnimationFrame(() => setIsIndicatorBumping(true));
    const bumpTimeoutId = window.setTimeout(setIsIndicatorBumping, INDICATOR_BUMP_DURATION_MS, false);
    const shakeTimeoutId = window.setTimeout(setShakingItemId, ITEM_SHAKE_DURATION_MS, null);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(bumpTimeoutId);
      window.clearTimeout(shakeTimeoutId);
    };
  }, [activeId]);

  useEffect(() => {
    const updateIndicator = () => {
      const container = containerRef.current;
      if (!container || !activeId) {
        setIndicator({ left: 0, width: 0, visible: false });
        return;
      }

      const activeItemElement = itemRefs.current?.[activeId];
      if (!activeItemElement) {
        setIndicator({ left: 0, width: 0, visible: false });
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const itemRect = activeItemElement.getBoundingClientRect();
      setIndicator({
        left: itemRect.left - containerRect.left,
        width: itemRect.width,
        visible: true,
      });
    };

    const frameId = window.requestAnimationFrame(updateIndicator);
    window.addEventListener('resize', updateIndicator);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateIndicator);
    };
  }, [activeId, containerRef, itemRefs]);

  return { indicatorBump: isIndicatorBumping, shakeId: shakingItemId, indicator };
}
